import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaBullseye, FaEye, FaBuilding, FaUsers, FaBox, 
  FaHeart, FaMapMarkerAlt, FaBolt, FaTrophy,
  FaBasketballBall, FaVolleyballBall, FaSwimmingPool,
  FaRunning, FaFutbol, FaMedal
} from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { aboutTranslations } from '../translations/about';
import { useManagedContent } from '../hooks/useManagedContent';
import { useManagedImages } from '../hooks/useManagedImages';
import { pageImageConfig } from '../content/pageImageConfig';
import '../App.css';

const featureIcons = [<FaBuilding />, <FaUsers />, <FaBox />, <FaHeart />, <FaMapMarkerAlt />, <FaBolt />];
const sportIcons = [<FaBasketballBall />, <FaVolleyballBall />, <FaFutbol />, <FaSwimmingPool />, <FaSwimmingPool />, <FaSwimmingPool />, <FaRunning />, <FaRunning />, <FaTrophy />, <FaMedal />, <FaMedal />, <FaTrophy />];
const statNumbers = ['50+', '10+', '5', '1000+'];

function About() {
  const { language } = useLanguage();
  const t = useManagedContent({
    pageKey: 'about',
    language,
    defaultTranslations: aboutTranslations
  });
  const images = useManagedImages({
    pageKey: 'about',
    defaultImages: pageImageConfig.about
  });

  const features = t.features.items.map((item, i) => ({ ...item, icon: featureIcons[i] }));
  const sports = t.sports.items.map((name, i) => ({ name, icon: sportIcons[i] }));
  const stats = statNumbers.map((number, i) => ({ number, label: t.commitment.stats[i] }));

  return (
    <div className="App">
      <Header />
      
      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-background">
          <img 
            src={images.hero}
            alt="Sports training facility"
            loading="lazy"
          />
          <div className="about-hero-overlay"></div>
        </div>
        <div className="about-hero-content">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <nav className="breadcrumb">
              <a href="/">{t.hero.home}</a> <span>/</span> <span>{t.hero.aboutUs}</span>
            </nav>
            <h1 className="about-hero-heading">{t.hero.heading}</h1>
            <h2 className="about-hero-subheading">{t.hero.subheading}</h2>
            <p className="about-hero-tagline">{t.hero.tagline}</p>
          </motion.div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="about-section">
        <div className="container">
          <div className="about-content-grid">
            <motion.div 
              className="about-text-content"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="section-heading">{t.whoWeAre.heading}</h2>
              <p className="section-text">{t.whoWeAre.text1}</p>
              <p className="section-text">{t.whoWeAre.text2}</p>
              <Link to="/locations">
                <motion.button 
                  className="btn-primary"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t.whoWeAre.ourLocations}
                </motion.button>
              </Link>
            </motion.div>
            <motion.div 
              className="about-image-content"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="about-image-wrapper">
                <img 
                  src={images.whoWeAre}
                  alt="Modern sports training facility"
                  loading="lazy"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Mission Section */}
      <section className="about-mission-section">
        <div className="mission-background">
          <img 
            src="https://i.postimg.cc/T1PtS4FS/Chat-GPT-Image-Jan-20-2026-12-52-28-PM.png" 
            alt="Mission background"
            loading="lazy"
          />
          <div className="mission-overlay"></div>
        </div>
        <div className="container">
          <motion.div 
            className="mission-content"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="mission-icon">
              <FaBullseye />
            </div>
            <h2 className="section-heading">{t.mission.heading}</h2>
            <p className="mission-text">{t.mission.text1}</p>
            <p className="mission-text-secondary">{t.mission.text2}</p>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Champion Maker Section */}
      <section className="about-features-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.features.heading}
          </motion.h2>
          <motion.p 
            className="section-subheading"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.features.subheading}
          </motion.p>
          <div className="about-features-grid">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="about-feature-card"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.02 }}
              >
                <div className="about-feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Vision Section */}
      <section className="about-vision-section">
        <div className="vision-background">
          <img 
            src="https://i.postimg.cc/d3Pv6Jgf/Chat-GPT-Image-Jan-20-2026-12-23-40-PM.png" 
            alt="Vision background"
            loading="lazy"
          />
          <div className="vision-overlay"></div>
        </div>
        <div className="container">
          <motion.div 
            className="vision-content"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="vision-icon">
              <FaEye />
            </div>
            <h2 className="section-heading">{t.vision.heading}</h2>
            <p className="vision-text">{t.vision.text1}</p>
            <p className="vision-text-secondary">{t.vision.text2}</p>
          </motion.div>
        </div>
      </section>

      {/* Sports We Serve Section */}
      <section className="about-sports-section">
        <div className="container">
          <motion.h2 
            className="section-heading"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.sports.heading}
          </motion.h2>
          <motion.p 
            className="section-subheading"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t.sports.subheading}
          </motion.p>
          <div className="sports-grid">
            {sports.map((sport, index) => (
              <motion.div 
                key={index}
                className="sport-badge"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                whileHover={{ scale: 1.1, backgroundColor: '#C49818', color: '#FFFFFF' }}
              >
                <span className="sport-icon">{sport.icon}</span>
                <span className="sport-name">{sport.name}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Commitment Section */}
      <section className="about-commitment-section">
        <div className="commitment-background">
          <img 
            src="https://i.postimg.cc/gkncpkWm/Chat-GPT-Image-Jan-20-2026-12-43-44-PM.png" 
            alt="Athletes training"
            loading="lazy"
          />
          <div className="commitment-overlay"></div>
        </div>
        <div className="container">
          <motion.div 
            className="commitment-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="commitment-heading">{t.commitment.heading}</h2>
            <p className="commitment-text">{t.commitment.text}</p>
            <div className="commitment-stats">
              {stats.map((stat, index) => (
                <motion.div 
                  key={index}
                  className="commitment-stat-item"
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="commitment-stat-number">{stat.number}</div>
                  <div className="commitment-stat-label">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="about-cta-section">
        <div className="container">
          <motion.div 
            className="about-cta-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="about-cta-heading">{t.cta.heading}</h2>
            <p className="about-cta-subheading">{t.cta.subheading}</p>
            <div className="about-cta-buttons">
              <Link to="/packages">
                <motion.button 
                  className="btn-primary"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t.cta.explorePackages}
                </motion.button>
              </Link>
              <Link to="/contact">
                <motion.button 
                  className="btn-secondary"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t.cta.contactUs}
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default About;
